'use client';
import { ClinicSearchResult, getAlgoliaSearchResponse } from '@/api/algolia';
import { MayBeSearchLocation, getDeliveryModeFilterFromQueryParameters, makeSearchParams } from '@/utils/search';
import { capitalizeWordsInString, getEnumValueFromString, getStringFromQueryParam } from '@/utils/string';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import Paginator from '@/app/_components/paginator';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { trackLicensedItemInSearchList } from '@/utils/trackingGtm';
import ListingFilters from './listingFilters';
import { ListingInfo } from './types';
import { convertSearchResponseIntoListingInfo } from '@/utils/listing';
import { MarkerLabelType } from '@/utils/maps';
import { ELButton, ELLoader } from '@davincihealthcare/elty-design-system-react';
import clsx from 'clsx/lite';
import Image from 'next/image';
import { DAVINCI_CDN } from '@/costants/defaultValues';
import { ListingOrder } from '@/app/_components/widgets/listing/listingOrder';
import { useListingOrder } from '@/app/_hooks/useListingOrder';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { getUserLocation, reverseGeocodeByCoordinates } from '@/api/mapbox';
import { BannerWelfare } from '@/app/_components/widgets/listing/BannerWelfare';
import { useHasScrolledOnce } from '@/app/_hooks/useHasScrolledOnce';
import { useHeaderHeight } from '@/app/_hooks/useHeaderHeight';
import ListingCard from '@/app/_components/widgets/listing/card';
import { formatPrice } from '@/utils/checkout';
import { formatDate } from '@/utils/dates';
import { ChannelFilterType, DateFilterType, PaymentFilterType, SortingListingType } from '@/api/algolia/types';
import { useFocusTrap } from '@/app/_hooks/useFocusTrap';
import { FlowType } from '@/types-aggregatore';
import CardFindBestClinic from './_components/CardFindBestClinic';
import { FIND_BEST_CLINIC_SEARCH_PARAM, FIND_BEST_CLINIC_SEARCH_PARAM_VALUE, invalidateFindBestClinicSearch } from './_constants';
import { usePostHogCardFindBestClinicFlag } from '@/app/_hooks/posthog/usePostHogCardFindBestClinicFlag';
import ListingMap from './ListingMap';
import { MarkerFocusChangedMessageData, PopupOpenedMessageData } from '@/app/_components/widgets/map/interactive/types';

type ListingProps = {
  service?: string;
  locationInfo?: MayBeSearchLocation;
  searchResultsServerSide?: ClinicSearchResult & { date: string };
  paginationPadding?: 'lg' | 'md';
  filterLight?: boolean;
  setLoadingState?: (isLoading: boolean) => void;
  loadingState?: boolean;
  showSearchNearYouCTA?: boolean;
  showSearchSection?: boolean;
  loadMapOnScroll?: boolean;
  showFindBestClinicCard?: boolean;
};

export const getGeoLocationParams = (
  params: ReadonlyURLSearchParams | null,
  { place, coordinates }: { place: string; coordinates: { lat: number; lng: number } },
) => {
  const newParams = new URLSearchParams(params?.toString());
  newParams.delete('page');
  newParams.set('location', place);
  newParams.set('lat', coordinates.lat?.toString());
  newParams.set('lng', coordinates.lng?.toString());
  newParams.set('userGeolocationActive', 'true');
  invalidateFindBestClinicSearch(newParams);

  return newParams;
};

const Listing = ({
  service,
  locationInfo,
  searchResultsServerSide,
  paginationPadding = 'lg',
  filterLight,
  setLoadingState,
  loadingState,
  showSearchNearYouCTA = false,
  showSearchSection = false,
  loadMapOnScroll = false,
  showFindBestClinicCard = false,
}: ListingProps) => {
  const params = useSearchParams();
  const isWelfare = useIsWelfare();
  const { shouldShow: shouldShowFindBestClinicCardFlag } = usePostHogCardFindBestClinicFlag();

  const isServerListing = useMemo(() => {
    return !!(searchResultsServerSide?.nbHits && !params?.size && !isWelfare);
  }, [isWelfare, params?.size, searchResultsServerSide?.nbHits]);

  const [listingInfo, setListingInfo] = useState<ListingInfo>(
    !!searchResultsServerSide?.nbHits ? convertSearchResponseIntoListingInfo(service, locationInfo?.name, searchResultsServerSide) : {},
  );

  const [location, setLocation] = useState<MayBeSearchLocation | undefined>(locationInfo);

  const [isShowingMap, setShowMap] = useState(false);
  const [hasMapLoaded, setMapLoaded] = useState(false);
  const shouldKeepMapOpenAfterActiveFilterRemovalRef = useRef(false);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  useFocusTrap({ trapRef: mapContainerRef, enabled: isShowingMap, onClose: () => setShowMap(false) });

  useEffect(() => {
    if (!hasMapLoaded && isShowingMap) {
      setMapLoaded(true);
    }
  }, [hasMapLoaded, isShowingMap]);

  useEffect(() => {
    if (!isShowingMap) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isShowingMap]);

  const [isShowingSearchNearYou, setIsShowingSearchNearYou] = useState(showSearchNearYouCTA);

  const locationQueryParam = params?.get('location') ?? '';
  const serviceSearchParam = params?.get('service') ?? '';
  const groupSlugQueryParam = getStringFromQueryParam(params?.get('groupslug') ?? '');
  const isFindBestClinicSearch =
    getStringFromQueryParam(params?.get(FIND_BEST_CLINIC_SEARCH_PARAM) ?? '') === FIND_BEST_CLINIC_SEARCH_PARAM_VALUE;
  const shouldShowFindBestClinicCard = shouldShowFindBestClinicCardFlag && showFindBestClinicCard && !isFindBestClinicSearch;
  const parsedPage = Number(params?.get('page') ?? '1');
  const isFirstPage = (isNaN(parsedPage) ? 1 : parsedPage) === 1;

  const hasLocation = !!(location || locationQueryParam);

  const headerService = serviceSearchParam || service;
  const headerLocation = locationQueryParam || location?.name;

  const { orderLabels, orderValuesLoading, setOrderLoading, setOrderValue, resetOrderValues } = useListingOrder();

  const [isSearchNearYouLoading, setIsSearchNearYouLoading] = useState(false);

  const searchInArea = useCallback(
    async (coordinates: { lat: number; lng: number }) => {
      try {
        const place = await reverseGeocodeByCoordinates(coordinates.lat, coordinates.lng);
        if (!place) return;

        const locationName = place.cityName ?? place.name;

        setLocation({
          latitude: `${place.latitude}`,
          longitude: `${place.longitude}`,
          name: locationName,
        });

        const placeCoordinates = { lat: place.latitude, lng: place.longitude };
        const newParams = getGeoLocationParams(params, { place: locationName, coordinates: placeCoordinates });
        window.history.pushState(null, '', `?${newParams.toString()}`);
        scrollToListingStart();
      } catch (error) {
        console.error('Error in reverse geocoding:', error);
      }
    },
    [params],
  );

  const searchNearYou = async () => {
    setIsSearchNearYouLoading(true);
    try {
      const currentLocation = await getUserLocation();
      if (!currentLocation) {
        setIsSearchNearYouLoading(false);
        return;
      }
      setLocation({
        latitude: currentLocation?.userCoordinates?.[0] ? `${currentLocation?.userCoordinates?.[0]}` : '',
        longitude: currentLocation?.userCoordinates?.[1] ? `${currentLocation?.userCoordinates?.[1]}` : '',
        name: currentLocation?.userLocationName,
      });
      scrollToListingStart();
      setIsSearchNearYouLoading(false);
      setIsShowingSearchNearYou(false);
    } catch (error) {
      console.error('Error checking location permission:', error);
      setIsSearchNearYouLoading(false);
      return null;
    }
  };

  const serverDateFormatted = useMemo(() => {
    if (!searchResultsServerSide?.date) return '';
    const date = new Date(searchResultsServerSide.date);
    // if Date is not valid
    if (isNaN(date.getTime())) return '';
    return capitalizeWordsInString(
      formatDate({
        date,
        localeOptions: { year: 'numeric', day: '2-digit', month: 'long' },
      }),
    );
  }, [searchResultsServerSide?.date]);

  const hasScrolled = useHasScrolledOnce();
  const headerHeight = useHeaderHeight();
  const shouldLoadMap = hasMapLoaded || (loadMapOnScroll ? hasScrolled : true);

  const userGeolocationActive = params?.get('userGeolocationActive');
  const isUserGeolocationActive = getStringFromQueryParam(userGeolocationActive ?? undefined) === 'true' ? true : false;

  const markerLabelType = useMemo<MarkerLabelType>(() => {
    const queryOrderFilter = getEnumValueFromString(SortingListingType, getStringFromQueryParam(params?.get('order') ?? ''));

    return queryOrderFilter === SortingListingType.firstAvailabilityAsc ? 'firstAvailability' : 'price';
  }, [params]);

  useEffect(() => {
    // Use server side results when possible
    if (isServerListing) {
      return;
    }

    let shouldIgnore = false;

    const doNewSearch = async () => {
      const parsedPage = Number(params?.get('page') ?? '1');
      const currentPage = isNaN(parsedPage) ? 1 : parsedPage;

      const queryDeliveryFilter = getDeliveryModeFilterFromQueryParameters(params?.get('exclude') ?? '');
      const queryOrderFilter = getEnumValueFromString(SortingListingType, getStringFromQueryParam(params?.get('order') ?? ''));

      if (queryOrderFilter === SortingListingType.priceAsc) {
        setOrderLoading(SortingListingType.priceAsc);
      }
      if (queryOrderFilter === SortingListingType.firstAvailabilityAsc) {
        setOrderLoading(SortingListingType.firstAvailabilityAsc);
      }
      if (queryOrderFilter === SortingListingType.distanceAsc) {
        setOrderLoading(SortingListingType.distanceAsc);
      }

      const channelFilter = params?.get('channelFilter')?.split(',');
      const paymentFilter = getStringFromQueryParam(params?.get('paymentFilter') ?? '');
      const dateFilter = getStringFromQueryParam(params?.get('dateFilter') ?? '');
      const { searchHealthcareService, searchLocation, searchPage, searchFilter, searchOrder } = await makeSearchParams(
        {
          service: serviceSearchParam,
          lat: params?.get('lat') ?? '',
          lng: params?.get('lng') ?? '',
          page: currentPage,
          location: locationQueryParam,
        },
        service,
        location,
        {
          excludeDomicilary: queryDeliveryFilter?.excludeDomicilary,
          excludeOnline: queryDeliveryFilter?.excludeOnline,
          excludeOnsite: queryDeliveryFilter?.excludeOnsite,
          groupSlug: groupSlugQueryParam || undefined,
          dateFilter: dateFilter as DateFilterType,
          channel: channelFilter as ChannelFilterType[],
          paymentType: paymentFilter as PaymentFilterType,
          availableFlowTypes: isWelfare ? FlowType.Welfare : undefined,
          excludePrice0: isWelfare,
        },
        queryOrderFilter,
      );

      const searchResponse = await getAlgoliaSearchResponse({
        text: searchHealthcareService,
        searchLocation,
        page: searchPage,
        filterObj: searchFilter,
        order: searchOrder,
        analyticsTags: ['booking-cerca'],
      });

      const newListingInfo = convertSearchResponseIntoListingInfo(searchHealthcareService, searchLocation?.name, searchResponse);
      // With this we make sure only the last search is applied
      if (!shouldIgnore) {
        setListingInfo(newListingInfo);
        trackLicensedItemInSearchList(newListingInfo);
        setLoadingState?.(false);

        if (isUserGeolocationActive && (newListingInfo.total ?? 0) <= 0 && !shouldKeepMapOpenAfterActiveFilterRemovalRef.current) {
          setShowMap(false);
        }
        shouldKeepMapOpenAfterActiveFilterRemovalRef.current = false;
        if (queryOrderFilter === SortingListingType.distanceAsc && isUserGeolocationActive) {
          setOrderValue(SortingListingType.distanceAsc, searchResponse);
        }
        if (queryOrderFilter === SortingListingType.priceAsc) {
          setOrderValue(SortingListingType.priceAsc, searchResponse);
        }
        if (queryOrderFilter === SortingListingType.firstAvailabilityAsc) {
          setOrderValue(SortingListingType.firstAvailabilityAsc, searchResponse);
        }
      }
    };

    doNewSearch();

    return () => {
      shouldIgnore = true;
    };
  }, [
    params,
    service,
    isServerListing,
    setOrderLoading,
    setOrderValue,
    location,
    isUserGeolocationActive,
    isWelfare,
    setLoadingState,
    serviceSearchParam,
    locationQueryParam,
    groupSlugQueryParam,
  ]);

  const listingInfoRef = useRef<HTMLDivElement>(null);
  const scrollToListingStart = () => {
    const offsetTop = 150;
    const targetPosition = listingInfoRef.current?.getBoundingClientRect().top ?? 0;
    const scrollPosition = window.scrollY + targetPosition - offsetTop;

    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  };

  const setLocationParams = useCallback(
    ({ place, coordinates }: { place: string; coordinates: [number, number] }) => {
      if (userGeolocationActive) return;
      const newParams = getGeoLocationParams(params, { place, coordinates: { lat: coordinates[0], lng: coordinates[1] } });
      window.history.pushState(null, '', `?${newParams.toString()}`);
    },
    [userGeolocationActive, params],
  );

  const keepMapOpenAfterActiveFilterRemoval = useCallback(() => {
    shouldKeepMapOpenAfterActiveFilterRemovalRef.current = true;
  }, []);

  useEffect(() => {
    if (!showSearchNearYouCTA) window.scrollTo(0, 0);
  }, [listingInfo.results, showSearchNearYouCTA]);

  useEffect(() => {
    resetOrderValues();
  }, [locationInfo, serviceSearchParam, resetOrderValues, locationQueryParam]);

  const [focusedItemIds, setFocusedItemIds] = useState<Set<string>>(new Set());
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  const onCardHoverChange = useCallback((id: string, hovered: boolean) => {
    setHoveredCardId(prev => {
      if (hovered) return id;
      return prev === id ? null : prev;
    });
  }, []);

  const onMarkerFocusChanged = useCallback((data: MarkerFocusChangedMessageData) => {
    setFocusedItemIds(prev => {
      const next = new Set(prev);
      if (data.isFocused) next.add(data.markerData.id);
      else next.delete(data.markerData.id);
      return next;
    });
  }, []);

  const onPopupOpened = useCallback(
    (_data: PopupOpenedMessageData) => {
      const mapEl = mapContainerRef.current;
      if (!mapEl) return;

      const rect = mapEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isFullyVisible = rect.top >= headerHeight && rect.bottom <= viewportHeight;
      if (isFullyVisible) return;

      window.scrollTo({
        top: window.scrollY + rect.top - headerHeight,
        behavior: 'smooth',
      });
    },
    [headerHeight],
  );

  return (
    <>
      {isShowingSearchNearYou && (
        <div className="flex items-center justify-center">
          {isSearchNearYouLoading ? (
            <ELLoader />
          ) : (
            <ELButton
              label={'Cerca vicino a te'}
              variant="filled"
              color="secondary"
              onClick={searchNearYou}
              leadingIcon={<MapPinIcon />}
              size="large"
              fullRadius
            />
          )}
        </div>
      )}

      {loadingState && (
        <div className="my-24 flex flex-col items-center gap-4">
          <Image
            src={`${DAVINCI_CDN}/inclinic/images/waiting.png`}
            width={600}
            height={600}
            alt="Icona caricamento ricerca"
            className="max-w-[300px]"
          />
          <div className="flex flex-col items-center justify-center gap-y-4">
            <p className="text-center text-lg font-bold" role="status">
              Ricerca in corso...
            </p>
            <ELLoader />
          </div>
        </div>
      )}

      <div className={`${loadingState ? 'hidden' : 'visible relative'}`}>
        {isWelfare && <BannerWelfare />}

        <ListingFilters filterLight={filterLight} showMap={() => setShowMap(true)} />

        <div role="status" aria-live="polite" className="sr-only">
          {listingInfo
            ? listingInfo?.total && listingInfo.total > 0
              ? `Sono stati trovati ${listingInfo.total} risultati`
              : 'Nessun risultato trovato'
            : 'Caricamento risultati in corso...'}
        </div>

        <div className="px-4 lg:pr-0 xl:pl-20">
          <div ref={listingInfoRef} className="grid grid-cols-1 gap-x-4 lg:grid-cols-[4fr_2fr] xl:gap-x-12">
            <div className="flex flex-col">
              <p className="text-xs font-semibold text-neutral-darker lg:mt-2 lg:text-sm">Ordina i risultati</p>
              {!!listingInfo?.results?.length && (
                <ListingOrder closestDistanceLabel={orderLabels.closestDistanceLabel} loadingOrderValues={orderValuesLoading} />
              )}

              <div className="flex flex-col gap-2 py-4">
                <p className="text-xs font-normal text-neutral-darker">
                  {[
                    listingInfo?.title || null,
                    isServerListing && searchResultsServerSide
                      ? `Prezzi da ${formatPrice({ amount: searchResultsServerSide.facets_stats?.['specializations.price']?.min })}`
                      : null,
                    isServerListing && searchResultsServerSide && serverDateFormatted ? (
                      <>
                        Aggiornato il&nbsp;
                        <time suppressHydrationWarning dateTime={searchResultsServerSide.date}>
                          {serverDateFormatted}
                        </time>
                      </>
                    ) : null,
                  ]
                    .filter(Boolean)
                    .map((segment, index) => (
                      <Fragment key={index}>
                        {index > 0 && ' • '}
                        {segment}
                      </Fragment>
                    ))}
                </p>
              </div>

              {/* listing body */}
              {!!listingInfo?.results?.length && (
                <div className="grid grid-rows-1">
                  <div className="grid grid-cols-1 items-start gap-y-4">
                    {shouldShowFindBestClinicCard && <CardFindBestClinic service={service} />}

                    {listingInfo.results.map((item, i) => {
                      const shouldShowBestClinicBanner = isFindBestClinicSearch && isFirstPage && i === 0;
                      return (
                        <ListingCard
                          key={item.objectID}
                          item={item}
                          showDistance={hasLocation}
                          showBestClinicBanner={shouldShowBestClinicBanner}
                          isFocused={focusedItemIds.has(item.objectID)}
                          onHoverChange={onCardHoverChange}
                        />
                      );
                    })}
                  </div>
                  <div className={paginationPadding === 'lg' ? `pt-48` : 'pt-10'}>
                    <Paginator totalPages={listingInfo?.pages} />
                  </div>
                </div>
              )}
            </div>
            {/* map: fullscreen overlay on mobile/tablet (toggled by isShowingMap), sticky column on desktop */}
            <div
              ref={mapContainerRef}
              className={clsx(
                isShowingMap ? 'block' : 'hidden',
                'fixed inset-0 z-[90] bg-white',
                'lg:relative lg:inset-auto lg:z-auto lg:flex lg:min-h-full lg:w-full lg:bg-transparent',
              )}
            >
              <div className="h-dvh w-full lg:sticky lg:top-0.5 lg:h-screen">
                {shouldLoadMap && (
                  <div className="h-full w-full">
                    <ListingMap
                      items={listingInfo.results ?? []}
                      markerLabelType={markerLabelType}
                      setUserGeolocation={setLocationParams}
                      isUserGeolocationActive={isUserGeolocationActive}
                      hoveredCardId={hoveredCardId}
                      onNewSearchInArea={searchInArea}
                      onMarkerFocusChanged={onMarkerFocusChanged}
                      onPopupOpened={onPopupOpened}
                      location={headerLocation}
                      service={headerService}
                      onBackClick={() => setShowMap(false)}
                      onActiveFilterRemove={keepMapOpenAfterActiveFilterRemoval}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Listing;
